import mapValues from 'lodash/mapValues';
import groupBy from 'lodash/groupBy';
import type { IUserPlanPreferences } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import Product, { AbsProduct, HycProduct, IgcProduct } from '@/creditflowresearch/domain/Product';
import Region, { ApacRegion, EmeaRegion, LatmRegion, UsoaRegion } from '@/creditflowresearch/domain/Region';
import Market, { EmergingMarket, EuropeanMarket, UsMarket } from '@/creditflowresearch/domain/Market';
import {
  isSegmentActive,
  isSegmentPreferenceItem,
  withResolvedSegments,
} from '@/creditflowresearch/application/hooks/userPreferences/segmentPreferences';
import { togglePreferenceId } from '@/creditflowresearch/application/helpers/togglePreferenceId';
import { FieldsNames, IBasePreferenceItem, IPreferenceItem, TIdListFieldName } from './types';

export { FieldsNames } from './types';
export type { TIdListFieldName } from './types';
export type { IBasePreferenceItem, IPreferenceItem } from './types';

const absRegionConfig: IBasePreferenceItem[] = [
  {
    id: ApacRegion.REGION_ID,
    fieldName: FieldsNames.Regions,
    getEntity() { return Region.from(ApacRegion.REGION_ID); },
  },
  {
    id: EmeaRegion.REGION_ID,
    fieldName: FieldsNames.Regions,
    getEntity() { return Region.from(EmeaRegion.REGION_ID); },
  },
  {
    id: LatmRegion.REGION_ID,
    fieldName: FieldsNames.Regions,
    getEntity() { return Region.from(LatmRegion.REGION_ID); },
  },
  {
    id: UsoaRegion.REGION_ID,
    isDefault: true,
    fieldName: FieldsNames.Regions,
    getEntity() { return Region.from(UsoaRegion.REGION_ID); },
  },
];

const corporateMarketConfig: IBasePreferenceItem[] = [
  {
    id: EuropeanMarket.MARKET_ID,
    fieldName: FieldsNames.Markets,
    getEntity() {
      const emeaMarket = Market.from(EuropeanMarket.MARKET_ID);

      return {
        getAbbreviation() { return 'EUROPE'; },
        getDisplayName() { return emeaMarket?.getDisplayName(); },
        canAccess(markets) { return emeaMarket?.canAccess(markets); },
        belongsToRegion() { return emeaMarket?.belongsToRegion(); },
      };
    },
  },
  {
    id: UsMarket.MARKET_ID,
    isDefault: true,
    fieldName: FieldsNames.Markets,
    getEntity() { return Market.from(UsMarket.MARKET_ID); },
  },
];

export const preferenceItems: IPreferenceItem[] = [
  {
    id: AbsProduct.PRODUCT_ID,
    fieldName: FieldsNames.Products,
    getEntity() { return Product.from(AbsProduct.PRODUCT_ID); },
    children: absRegionConfig,
  },
  {
    id: HycProduct.PRODUCT_ID,
    fieldName: FieldsNames.Products,
    getEntity() { return Product.from(HycProduct.PRODUCT_ID); },
    children: corporateMarketConfig,
  },
  {
    id: IgcProduct.PRODUCT_ID,
    fieldName: FieldsNames.Products,
    getEntity() { return Product.from(IgcProduct.PRODUCT_ID); },
    children: corporateMarketConfig,
  },
  {
    id: EmergingMarket.MARKET_ID,
    fieldName: FieldsNames.Markets,
    getEntity() { return Market.from(EmergingMarket.MARKET_ID); },
    children: [
      {
        id: ApacRegion.REGION_ID,
        isDefault: true,
        fieldName: FieldsNames.Regions,
        getEntity() {
          const region = Region.from(ApacRegion.REGION_ID);

          return {
            getAbbreviation() { return 'ASIA'; },
            getDisplayName() { return region?.getDisplayName(); },
            canAccess(regions) { return region?.canAccess(regions); },
          };
        },
      },
      {
        id: EmeaRegion.REGION_ID,
        isDefault: true,
        fieldName: FieldsNames.Regions,
        getEntity() {
          const region = Region.from(EmeaRegion.REGION_ID);

          return {
            getAbbreviation() { return 'CEEMEA'; },
            getDisplayName() { return region?.getDisplayName(); },
            canAccess(regions) { return region?.canAccess(regions); },
          };
        },
      },
      {
        id: LatmRegion.REGION_ID,
        isDefault: true,
        fieldName: FieldsNames.Regions,
        getEntity() { return Region.from(LatmRegion.REGION_ID); },
      },
    ],
  },
];

export const canAccessItem = (
  item: IPreferenceItem,
  accessibleProducts: string[] = [],
  accessibleRegions: string[] = [],
) => {
  const mapAccessibleEntity = {
    [FieldsNames.Products]: accessibleProducts,
    [FieldsNames.Regions]: accessibleRegions,
  };
  const entity = item.getEntity();
  const canChildrenAccess = item.children
    ? item.children.some((child) => canAccessItem(child, accessibleProducts, accessibleRegions))
    : true;

  if (item.fieldName === FieldsNames.Markets && entity) {
    const region = (entity as Market).belongsToRegion();

    if (item.children) {
      return canChildrenAccess;
    }

    return region?.canAccess(accessibleRegions) ?? false;
  }

  return canChildrenAccess && (entity?.canAccess(mapAccessibleEntity[item.fieldName] || []) ?? false);
};

const getCurrentActiveItems = (
  items: IPreferenceItem[],
  canAccess: (item: IPreferenceItem) => boolean,
  canActive: (item: IPreferenceItem) => boolean,
) => {
  return items.filter((item) => canActive(item) && canAccess(item));
};

const getDefaultItems = (
  items: IPreferenceItem[],
  canAccess: (item: IPreferenceItem) => boolean,
) => {
  return items.filter((item) => item.isDefault && canAccess(item));
};

const getFirstEnabledItem = (
  items: IPreferenceItem[],
  canAccess: (item: IPreferenceItem) => boolean,
) => {
  return items.find(canAccess);
};

const getActiveItems = (
  items: IPreferenceItem[],
  canAccess: (item: IPreferenceItem) => boolean,
  canActive: (item: IPreferenceItem) => boolean,
): IPreferenceItem[] => {
  if (!items?.length) return [];

  const activeItems = getCurrentActiveItems(items, canAccess, canActive);
  if (activeItems.length) return activeItems;

  const defaultItems = getDefaultItems(items, canAccess);
  if (defaultItems.length) return defaultItems;

  const firstEnabled = getFirstEnabledItem(items, canAccess);
  if (firstEnabled) return [firstEnabled];

  return [];
};

export const getDefaultItem = (
  items: IPreferenceItem[],
  canAccess: (item: IPreferenceItem) => boolean,
) => {
  if (!items?.length) return null;

  const defaultItems = getDefaultItems(items, canAccess);
  if (defaultItems.length) return defaultItems[0];

  const firstEnabled = getFirstEnabledItem(items, canAccess);
  if (firstEnabled) return firstEnabled;

  return null;
};

const toIdsByFieldName = (items: IPreferenceItem[]): Partial<Record<TIdListFieldName, string[]>> => mapValues(
  groupBy(items, 'fieldName'),
  (groupedItems) => groupedItems.map(({ id }) => id),
);

export const getMapActiveItemIds = (
  items: IPreferenceItem[],
  canAccess: (item: IPreferenceItem) => boolean,
  canActive: (item: IPreferenceItem) => boolean,
) => toIdsByFieldName(getActiveItems(items, canAccess, canActive));

/** Segments live in keyed groups, so only the id-list fields can be read as a flat array. */
export const getPreferenceIds = (
  preferences: Partial<IUserPlanPreferences>,
  fieldName: FieldsNames,
): string[] => (fieldName === FieldsNames.Segments ? [] : preferences[fieldName] ?? []);

export const isPreferenceActive = (
  preferences: Partial<IUserPlanPreferences>,
  item: IBasePreferenceItem,
): boolean => (isSegmentPreferenceItem(item)
  ? isSegmentActive(preferences, item)
  : getPreferenceIds(preferences, item.fieldName).includes(item.id));

/**
 * Saved preferences may point at a product the user has since lost access to. Such an
 * item must not read as selected: the selection falls back to the first accessible
 * item (ABS locked -> HYC, ABS and HYC locked -> IGC) together with that item's own
 * accessible children, so display, redirects and further edits all share one
 * consistent selection.
 */
export const resolveAccessiblePreferences = (
  items: IPreferenceItem[],
  preferences: Partial<IUserPlanPreferences>,
  canAccess: (item: IPreferenceItem) => boolean,
): Partial<IUserPlanPreferences> => {
  const canActive = (item: IBasePreferenceItem) => isPreferenceActive(preferences, item);
  const activeItems = getActiveItems(items, canAccess, canActive);
  const [activeItem] = activeItems;

  if (!activeItem) return {};

  return withResolvedSegments({
    ...toIdsByFieldName(activeItems),
    ...getMapActiveItemIds(activeItem.children || [], canAccess, canActive),
  }, preferences);
};

const getChildrenSignature = (item: IPreferenceItem): string => {
  if (!item.children?.length) return '';

  return item.children.map(({ id }) => id).sort().join(',');
};

const findCompatibleItemIds = (
  newItem: IPreferenceItem,
  allItems: IPreferenceItem[],
  existingIds: string[],
): string[] => {
  const newSignature = getChildrenSignature(newItem);

  return allItems
    .filter((item) =>
      item.fieldName === newItem.fieldName &&
      existingIds.includes(item.id) &&
      getChildrenSignature(item) === newSignature,
    )
    .map((item) => item.id);
};

export const mergePreferenceIds = (
  newItem: IPreferenceItem,
  allItems: IPreferenceItem[],
  existingIds: string[],
): string[] => {
  if (!existingIds.length) return [newItem.id];

  const compatibleIds = findCompatibleItemIds(newItem, allItems, existingIds);

  return togglePreferenceId(newItem.id, compatibleIds);
};
